import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "portfolio", label: t.nav.portfolio },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), 60);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-100"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        {/* Brand */}
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/monogram.png"
            alt="Ing. Alessandro Perago"
            className={`h-10 w-auto transition-all duration-500 ${
              scrolled ? "" : "brightness-0 invert"
            }`}
          />
          <span
            className={`font-display font-bold tracking-tight text-[15px] leading-none hidden sm:block ${
              scrolled ? "text-[#0B1F33]" : "text-white"
            }`}
          >
            Ing. Alessandro Perago
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-[13px] font-medium tracking-wide relative py-1 transition-colors duration-300 group ${
                scrolled ? "text-[#1F2937] hover:text-[#0B1F33]" : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-[#B87333] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div
            data-testid="lang-switcher"
            className={`hidden sm:flex items-center text-[12px] font-semibold border ${
              scrolled ? "border-[#0B1F33]/15" : "border-white/30"
            }`}
          >
            {["it", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => setLang(code)}
                className={`px-2.5 py-1.5 uppercase tracking-wider transition-colors duration-200 ${
                  lang === code
                    ? "bg-[#B87333] text-white"
                    : scrolled
                    ? "text-[#0B1F33]"
                    : "text-white/80"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          {/* CTA */}
          <button
            data-testid="header-cta"
            onClick={() => go("contact")}
            className="hidden md:inline-flex items-center bg-[#0B1F33] text-white text-[13px] font-semibold px-5 py-2.5 hover:bg-[#B87333] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((p) => !p)}
            className={`lg:hidden grid place-items-center w-10 h-10 ${
              scrolled ? "text-[#0B1F33]" : "text-white"
            }`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-[15px] font-medium text-[#1F2937] border-b border-gray-50"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center border border-[#0B1F33]/15 text-[12px] font-semibold">
                  {["it", "en"].map((code) => (
                    <button
                      key={code}
                      data-testid={`mobile-lang-${code}`}
                      onClick={() => setLang(code)}
                      className={`px-3 py-2 uppercase tracking-wider ${
                        lang === code ? "bg-[#B87333] text-white" : "text-[#0B1F33]"
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="mobile-cta"
                  onClick={() => go("contact")}
                  className="bg-[#0B1F33] text-white text-[13px] font-semibold px-5 py-2.5"
                >
                  {t.nav.cta}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
